import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { pickupSheetApi, absoluteFileUrl } from "@/lib/phase3Entities";
import { api } from "@/lib/api";
import { apiError } from "@/lib/errors";
import {
  CheckCircle2,
  AlertTriangle,
  Clock3,
  FileText,
  Eye,
  ShieldCheck,
  Printer,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  CircleAlert,
  Info,
  Download,
  MessageSquare,
} from "lucide-react";

/**
 * PickupSheetsTab — Canonical Pickup Sheet workflow UI (7A-1 + 7A-2 + 7A-3).
 *
 * 7A-3 additions:
 *   - Responsive layout: mobile 390, tablet 768, desktop 1440
 *   - Deep-link targeting via ?da= search param → scroll + highlight
 *   - Touch-target safe action buttons (min-h-[44px] on mobile)
 */

const STATUS_CONFIG = {
  NEEDS_INFORMATION:        { label: "Needs Information",        icon: AlertTriangle,  color: "text-amber-hold",   bg: "bg-amber-hold/10" },
  NEEDS_CLIENT_APPROVAL:    { label: "Needs Client Approval",    icon: Clock3,         color: "text-coach-blue",   bg: "bg-coach-blue/10" },
  NEEDS_OFFICE_APPROVAL:    { label: "Needs Office Approval",    icon: Clock3,         color: "text-coach-blue",   bg: "bg-coach-blue/10" },
  CLIENT_CHANGES_REQUESTED: { label: "Client Changes Requested", icon: CircleAlert,    color: "text-critical-red", bg: "bg-critical-red/10" },
  CHANGED_AFTER_APPROVAL:   { label: "Changed After Approval",   icon: AlertTriangle,  color: "text-amber-hold",   bg: "bg-amber-hold/10" },
  APPROVED_PDF_PENDING:     { label: "Approved — PDF Pending",   icon: RefreshCw,      color: "text-amber-hold",   bg: "bg-amber-hold/10" },
  READY_TO_PRINT:           { label: "Ready to Print",           icon: Printer,        color: "text-touring-lime", bg: "bg-touring-lime/10" },
  PRINTED_PROVIDED:         { label: "Printed & Provided",       icon: CheckCircle2,   color: "text-touring-lime", bg: "bg-touring-lime/10" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status || "Unknown", icon: Info, color: "text-steel", bg: "bg-chrome-mist" };
  const Icon = cfg.icon;
  return (
    <span
      data-testid="pickup-workflow-status"
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-sm whitespace-nowrap ${cfg.bg} ${cfg.color}`}
    >
      <Icon size={12} className="shrink-0" /> {cfg.label}
    </span>
  );
}

function ReadinessCard({ readiness }) {
  if (!readiness) return null;
  const { ready, missing = [], conditionals = {} } = readiness;

  return (
    <div
      data-testid="pickup-readiness-card"
      className={`rounded-sm border p-3 text-[12.5px] ${
        ready ? "border-touring-lime/40 bg-touring-lime/5" : "border-amber-hold/40 bg-amber-hold/5"
      }`}
    >
      <div className="flex items-center gap-1.5 font-medium">
        {ready ? (
          <>
            <CheckCircle2 size={13} className="text-touring-lime shrink-0" />
            <span className="text-carbon dark:text-road-white">All required fields complete</span>
          </>
        ) : (
          <>
            <AlertTriangle size={13} className="text-amber-hold shrink-0" />
            <span className="text-carbon dark:text-road-white">
              {missing.length} missing field{missing.length !== 1 ? "s" : ""}
            </span>
          </>
        )}
      </div>
      {missing.length > 0 && (
        <ul className="mt-2 space-y-1 pl-5 list-disc text-steel">
          {missing.map((m, i) => (
            <li key={i} data-testid={`pickup-missing-${i}`} className="break-words">
              {m.fix_link ? (
                <Link
                  to={m.fix_link}
                  className="text-coach-blue hover:underline break-words"
                  data-testid={`pickup-fix-link-${i}`}
                >
                  {m.label || m.field}
                </Link>
              ) : (
                <span className="text-carbon dark:text-road-white break-words">{m.label || m.field}</span>
              )}
              {m.source && <span className="text-steel"> — {m.source}</span>}
            </li>
          ))}
        </ul>
      )}
      {(conditionals.trailer || conditionals.co_driver) && (
        <div className="mt-2 text-[11px] text-steel">
          {conditionals.trailer && <span className="mr-3">Trailer: conditional</span>}
          {conditionals.co_driver && <span>Co-Driver: conditional</span>}
        </div>
      )}
    </div>
  );
}

function ApprovalInfo({ label, approval, testIdPrefix }) {
  if (!approval) return null;
  return (
    <div data-testid={`${testIdPrefix}-info`} className="text-[11px] text-steel break-words">
      <span className="font-medium text-carbon dark:text-road-white">{label}:</span>{" "}
      {approval.status === "approved" ? (
        <span className="text-touring-lime">Approved</span>
      ) : (
        <span className="text-critical-red">Changes Requested</span>
      )}
      {approval.actor_email && <span className="break-all"> by {approval.actor_email}</span>}
      {approval.approved_at && (
        <span> · {new Date(approval.approved_at).toLocaleDateString()}</span>
      )}
      {approval.comment && (
        <div
          className="flex items-start gap-1 mt-1 pl-2 border-l-2 border-coach-silver text-carbon dark:text-road-white break-words"
          data-testid={`${testIdPrefix}-comment`}
        >
          <MessageSquare size={10} className="text-steel mt-0.5 shrink-0" />
          <span className="italic min-w-0 break-words">{approval.comment}</span>
        </div>
      )}
    </div>
  );
}

function FingerprintIndicator({ label, current, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 whitespace-nowrap ${current ? "text-touring-lime" : "text-steel"}`}
    >
      {current ? <CheckCircle2 size={11} className="shrink-0" /> : <Clock3 size={11} className="shrink-0" />}
      {label}: {current ? "Current" : "Pending"}
    </span>
  );
}

/** Fetch authenticated PDF blob and open in new tab. */
async function openAuthPdf(url) {
  const resp = await api.get(url, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const objUrl = URL.createObjectURL(blob);
  window.open(objUrl, "_blank");
  setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
}

/* ------------------------------------------------------------------ */
/*  ACTION BUTTON — shared responsive style                           */
/* ------------------------------------------------------------------ */
const BTN_BASE =
  "inline-flex items-center justify-center gap-1 text-[12px] rounded-sm transition " +
  "min-h-[36px] sm:min-h-0 px-2.5 py-1.5 " +
  "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-coach-blue";

function DriverWorkflowCard({ tourId, assignment, canWrite, driverName, coachName, trailerName, isTarget }) {
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const cardRef = useRef(null);

  const loadWorkflow = useCallback(async () => {
    try {
      const wf = await pickupSheetApi.workflow(tourId, assignment.id);
      setWorkflow(wf);
    } catch {
      setWorkflow(null);
    } finally {
      setLoading(false);
    }
  }, [tourId, assignment.id]);

  useEffect(() => {
    loadWorkflow();
  }, [loadWorkflow]);

  // Deep-link target: scroll into view + highlight
  useEffect(() => {
    if (isTarget && cardRef.current) {
      const el = cardRef.current;
      // Small delay to let the tab panel render
      const t = setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlight(true);
        el.focus({ preventScroll: true });
      }, 300);
      // Remove highlight after 2.5s
      const t2 = setTimeout(() => setHighlight(false), 3000);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
  }, [isTarget]);

  /* --- 7A-2 action handlers (zero business mutations on render) --- */

  const handlePreview = async () => {
    try {
      const relUrl = `/tours/${tourId}/pickup-sheets/${assignment.id}/preview`;
      await openAuthPdf(relUrl);
      pickupSheetApi.logPreview(tourId, assignment.id);
    } catch (e) {
      toast.error(apiError(e) || "Preview failed");
    }
  };

  const handleOfficeApprove = async () => {
    setBusy(true);
    try {
      await pickupSheetApi.officeApprove(tourId, assignment.id);
      toast.success("Office approval recorded");
      loadWorkflow();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRetryPdf = async () => {
    setBusy(true);
    try {
      await pickupSheetApi.retryFinalPdf(tourId, assignment.id);
      toast.success("Final PDF generated");
      loadWorkflow();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadFinal = async () => {
    if (!workflow?.final_pickup_sheet_id) return;
    try {
      const sheets = await pickupSheetApi.list(tourId);
      const final = sheets.find((s) => s.id === workflow.final_pickup_sheet_id);
      if (final?.pdf_url) {
        await openAuthPdf(final.pdf_url);
      } else {
        toast.error("Final PDF not available");
      }
    } catch (e) {
      toast.error(apiError(e) || "Download failed");
    }
  };

  const handleMarkPrinted = async () => {
    setBusy(true);
    try {
      await pickupSheetApi.markPrintedProvided(tourId, assignment.id);
      toast.success("Marked as Printed & Provided to Driver");
      loadWorkflow();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const status = workflow?.status;
  const readiness = workflow?.readiness;
  const hasFinalPdf = !!workflow?.final_pickup_sheet_id;

  return (
    <div
      ref={cardRef}
      id={`pickup-card-${assignment.id}`}
      tabIndex={-1}
      data-testid={`pickup-workflow-${assignment.id}`}
      className={
        "bg-white dark:bg-graphite border rounded-md outline-none transition-shadow duration-500 min-w-0 overflow-hidden " +
        (highlight
          ? "border-coach-blue ring-2 ring-coach-blue/40 shadow-md"
          : "border-coach-silver dark:border-graphite")
      }
    >
      {/* Header — stacks on mobile for clean wrap */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 border-b border-coach-silver/40">
        <div className="flex items-start gap-2 min-w-0">
          <FileText size={14} className="text-coach-blue shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-carbon dark:text-road-white truncate" data-testid="pickup-driver-name">
              {driverName || `Driver ${assignment.driver_id?.slice(0, 8)}…`}
            </div>
            <div className="text-[11px] text-steel flex flex-wrap gap-x-3 gap-y-0.5 min-w-0">
              {coachName && <span data-testid="pickup-coach-name" className="break-words">Coach: {coachName}</span>}
              {trailerName && <span data-testid="pickup-trailer-name" className="break-words">Trailer: {trailerName}</span>}
              <span className="whitespace-nowrap">{assignment.start_date || "—"} → {assignment.end_date || "—"}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center pl-6 sm:pl-0">
          {loading ? (
            <span className="text-[11px] text-steel">Loading…</span>
          ) : status ? (
            <StatusBadge status={status} />
          ) : (
            <span className="text-[11px] text-steel">No workflow data</span>
          )}
        </div>
      </div>

      {/* Body */}
      {!loading && workflow && (
        <div className="px-4 py-3 space-y-3 min-w-0">
          <ReadinessCard readiness={readiness} />

          {/* Approval info */}
          <div className="space-y-1.5">
            <ApprovalInfo
              label="Client Approval"
              approval={workflow.last_client_approval}
              testIdPrefix="pickup-client-approval"
            />
            <ApprovalInfo
              label="Office Approval"
              approval={workflow.last_office_approval}
              testIdPrefix="pickup-office-approval"
            />
          </div>

          {/* Fingerprint + departure status */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
            <FingerprintIndicator label="Client" current={workflow.client_current} testId="pickup-client-current" />
            <FingerprintIndicator label="Office" current={workflow.office_current} testId="pickup-office-current" />
            <FingerprintIndicator label="Distribution" current={workflow.distribution_current} testId="pickup-distribution-current" />
            {workflow.departure_checklist_complete && (
              <span className="inline-flex items-center gap-1 text-touring-lime whitespace-nowrap" data-testid="pickup-departure-complete">
                <ShieldCheck size={11} className="shrink-0" /> Departure gate satisfied
              </span>
            )}
          </div>

          {/* Final version */}
          {hasFinalPdf && (
            <div className="text-[11px] text-steel" data-testid="pickup-final-version">
              <FileText size={11} className="inline mr-1" />
              Final version: v{workflow.final_pickup_sheet_version}
            </div>
          )}

          {/* Actions — grid on mobile for safe touch targets */}
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" onClick={handlePreview} data-testid={`pickup-preview-${assignment.id}`}
              className={`${BTN_BASE} border border-coach-silver hover:border-coach-blue text-carbon dark:text-road-white`}>
              <Eye size={12} /> Preview PDF
            </button>

            {canWrite && status === "NEEDS_OFFICE_APPROVAL" && (
              <button type="button" onClick={handleOfficeApprove} disabled={busy}
                data-testid={`pickup-office-approve-${assignment.id}`}
                className={`${BTN_BASE} bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-40`}>
                <ShieldCheck size={12} /> {busy ? "Approving…" : "Office Approve"}
              </button>
            )}

            {canWrite && status === "APPROVED_PDF_PENDING" && (
              <button type="button" onClick={handleRetryPdf} disabled={busy}
                data-testid={`pickup-retry-pdf-${assignment.id}`}
                className={`${BTN_BASE} bg-amber-hold hover:bg-amber-hold/80 text-carbon disabled:opacity-40 focus:ring-amber-hold`}>
                <RefreshCw size={12} /> {busy ? "Retrying…" : "Retry PDF"}
              </button>
            )}

            {hasFinalPdf && (status === "READY_TO_PRINT" || status === "PRINTED_PROVIDED") && (
              <button type="button" onClick={handleDownloadFinal}
                data-testid={`pickup-download-final-${assignment.id}`}
                className={`${BTN_BASE} border border-coach-silver hover:border-coach-blue text-carbon dark:text-road-white`}>
                <Download size={12} /> Download / Print
              </button>
            )}

            {canWrite && status === "READY_TO_PRINT" && (
              <button type="button" onClick={handleMarkPrinted} disabled={busy}
                data-testid={`pickup-mark-printed-${assignment.id}`}
                className={`${BTN_BASE} bg-touring-lime hover:bg-touring-lime/80 text-carbon disabled:opacity-40 focus:ring-touring-lime`}>
                <Printer size={12} /> {busy ? "Marking…" : "Mark Printed & Provided"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Version history toggle */}
      {!loading && (
        <div className="border-t border-coach-silver/40">
          <button type="button" onClick={() => setExpanded((p) => !p)}
            data-testid={`pickup-versions-toggle-${assignment.id}`}
            className="w-full flex items-center gap-1 px-4 py-2 text-[11px] text-steel hover:text-coach-blue transition focus:outline-none focus:ring-1 focus:ring-coach-blue min-h-[36px] sm:min-h-0">
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Version History
          </button>
          {expanded && <VersionHistory tourId={tourId} driverAssignmentId={assignment.id} />}
        </div>
      )}
    </div>
  );
}

function VersionHistory({ tourId, driverAssignmentId }) {
  const [sheets, setSheets] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    pickupSheetApi
      .list(tourId)
      .then((all) => {
        setSheets(all.filter((s) => s.driver_assignment_id === driverAssignmentId));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [tourId, driverAssignmentId]);

  if (!loaded) return <div className="px-4 pb-2 text-[11px] text-steel">Loading…</div>;
  if (sheets.length === 0) return <div className="px-4 pb-2 text-[11px] text-steel">No versions yet.</div>;

  return (
    <ul className="px-4 pb-3 space-y-1">
      {sheets.map((s) => (
        <li key={s.id} className="flex flex-wrap items-center gap-2 text-[12px]" data-testid={`pickup-version-${s.id}`}>
          <span className="font-medium text-carbon dark:text-road-white">v{s.version}</span>
          <span className="font-mono text-[10.5px] text-steel truncate max-w-[160px] sm:max-w-[200px]">
            {s.pdf_sha256?.slice(0, 24)}…
          </span>
          {s.pdf_url && (
            <a href={absoluteFileUrl(s.pdf_url)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-0.5 text-coach-blue hover:underline"
              data-testid={`pickup-view-${s.id}`}>
              <ExternalLink size={10} /> Open
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function PickupSheetsTab({ tourId, drivers, allDrivers = [], tourAssets = [], allAssets = [], canWrite = true, targetAssignmentId }) {
  const primaries = (drivers || []).filter((d) => d.role === "primary_driver");

  // Also read ?da= from URL if targetAssignmentId not passed
  const [searchParams] = useSearchParams();
  const daParam = targetAssignmentId || searchParams.get("da");

  // Resolve human-readable names from parent data
  const driverLookup = Object.fromEntries((allDrivers || []).map((d) => [d.id, d.display_name]));
  const assetLookup = Object.fromEntries((allAssets || []).map((a) => [a.id, a]));

  // Resolve coach/trailer: try allAssets first, fall back to tourAsset embedded data
  let coachName = null;
  let trailerName = null;
  for (const ta of tourAssets) {
    const asset = assetLookup[ta.asset_id];
    const name = asset?.unit_name || asset?.display_name || ta.unit_name || ta.asset_name || ta.display_name;
    const type = asset?.asset_type || ta.asset_type;
    if (!name) continue;
    if (type === "trailer") {
      trailerName = trailerName || name;
    } else {
      coachName = coachName || name;
    }
  }

  return (
    <div className="space-y-4 min-w-0" data-testid="pickup-sheets-tab">
      <div className="bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-3 sm:p-4 min-w-0 overflow-hidden">
        <div className="text-[10.5px] uppercase tracking-widest text-steel">
          Driver Pickup Sheets — one per primary driver assignment
        </div>
        <div className="text-[11.5px] text-steel mt-1">
          Sheets embed only public co-driver info (display name + operational phone) —
          CDL / medical / address / rates are never included.
        </div>
      </div>

      {primaries.length === 0 ? (
        <div
          className="bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-6 text-center text-[13px] text-steel"
          data-testid="pickup-no-assignments"
        >
          No primary driver assignments on this tour yet.
        </div>
      ) : (
        primaries.map((d) => (
          <DriverWorkflowCard
            key={d.id}
            tourId={tourId}
            assignment={d}
            canWrite={canWrite}
            driverName={driverLookup[d.driver_id]}
            coachName={coachName}
            trailerName={trailerName}
            isTarget={daParam === d.id}
          />
        ))
      )}
    </div>
  );
}
